import React, {Component} from 'react';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import XLSX from 'xlsx/dist/xlsx.full.min.js';
import Chart from '../../../utils/chart';
import PageTitle from "../../../components/common/PageTitle";
import FeatherIcon from 'feather-icons-react';
import {FormSelect} from "shards-react";
import './analysisReport.scss';

const WD_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
const WOM_LABELS = ['첫째주', '둘째주', '셋째주', '넷째주', '다섯째주']; // 월 내 주차(1~7,8~14,15~21,22~28,29~31일)

/**
 * 분석 리포트 (스토어 모니터/분석 > 분석 리포트) — SuperAdmin 전용
 *
 * public/analysis/ 의 연도별 엑셀(visit_YYYY.xls / install_YYYY.xls …)을 클라이언트에서 파싱하여
 * 지표(방문 트래픽·앱 설치 등)별로 월별 최다 월 / 요일별 최다 요일을 그래프로 시각화한다.
 *
 * 엑셀 구조(가로형 2행): ["구분", ...엑셀날짜시리얼], ["트래픽"/"설치 건수", ...일별 수치].
 * 날짜는 manifest 의 year(파일명 기준 연도)를 기준으로 표시/요일 계산한다.
 * 새 데이터 추가: public/analysis/ 에 .xls 추가 + manifest.json 의 datasets 에 한 줄 추가.
 */
@observer
class AnalysisReport extends Component {

  @observable datasets = [];        // [{metric, unit, year, file}]
  @observable selectedMetric = '';
  @observable selectedYear = '';
  @observable currentMeta = {metric: '', unit: '건', year: ''};
  @observable loading = true;
  @observable error = '';
  @observable report = null;        // 집계 결과

  monthlyRef = React.createRef();
  weekdayRef = React.createRef();
  womRef = React.createRef();
  dailyRef = React.createRef();
  _charts = {};

  componentDidMount() {
    this.loadManifest();
  }

  componentWillUnmount() {
    Object.keys(this._charts).forEach(k => { try { this._charts[k].destroy(); } catch (e) {} });
  }

  _base = () => (process.env.PUBLIC_URL || '') + '/analysis/';

  // === manifest 파생 ===
  metricsList = () => {
    const seen = {}, out = [];
    this.datasets.forEach(d => { if (d.metric && !seen[d.metric]) { seen[d.metric] = 1; out.push(d.metric); } });
    return out;
  };
  yearsForMetric = (metric) => this.datasets
    .filter(d => d.metric === metric)
    .map(d => String(d.year))
    .sort((a, b) => b.localeCompare(a));
  findDataset = (metric, year) => this.datasets.find(d => d.metric === metric && String(d.year) === String(year));

  @action loadManifest = async () => {
    this.loading = true;
    this.error = '';
    try {
      const res = await fetch(this._base() + 'manifest.json', {cache: 'no-cache'});
      const json = await res.json();
      let list = (json && Array.isArray(json.datasets)) ? json.datasets.slice() : [];
      list = list.filter(d => d && d.file);
      this.datasets = list;
      if (list.length === 0) {
        this.loading = false;
        this.error = '분석할 데이터셋이 없습니다. public/analysis/manifest.json 을 확인하세요.';
        return;
      }
      const metric = this.metricsList()[0];
      const year = this.yearsForMetric(metric)[0];
      this.selectedMetric = metric;
      this.selectedYear = year;
      await this.loadCurrent();
    } catch (e) {
      this.loading = false;
      this.error = '데이터셋 목록(manifest.json)을 불러오지 못했습니다.';
    }
  };

  @action onChangeMetric = (metric) => {
    this.selectedMetric = metric;
    const years = this.yearsForMetric(metric);
    if (years.indexOf(this.selectedYear) === -1) this.selectedYear = years[0];
    this.loadCurrent();
  };
  @action onChangeYear = (year) => { this.selectedYear = year; this.loadCurrent(); };

  @action loadCurrent = async () => {
    const ds = this.findDataset(this.selectedMetric, this.selectedYear);
    if (!ds) { this.error = '선택한 데이터셋을 찾을 수 없습니다.'; this.report = null; this.loading = false; return; }
    this.loading = true;
    this.error = '';
    this.currentMeta = {metric: ds.metric, unit: ds.unit || '건', year: String(ds.year)};
    try {
      const res = await fetch(this._base() + ds.file, {cache: 'no-cache'});
      if (!res.ok) throw new Error('fetch ' + res.status);
      const buf = await res.arrayBuffer();
      const report = this.parseWorkbook(buf, String(ds.year));
      if (!report || report.validDays === 0) {
        this.report = null;
        this.error = '엑셀에서 유효한 일별 데이터를 찾지 못했습니다.';
      } else {
        this.report = report;
      }
      this.loading = false;
      this.renderCharts();
    } catch (e) {
      this.loading = false;
      this.report = null;
      this.error = '엑셀 파일(' + ds.file + ')을 불러오거나 분석하지 못했습니다.';
    }
  };

  // 가로형 2행 엑셀 → 월/요일/일별 집계. 연도(year)는 파일명 기준 연도로 통일.
  parseWorkbook = (arrayBuffer, year) => {
    const fileYear = parseInt(year, 10);
    const wb = XLSX.read(new Uint8Array(arrayBuffer), {type: 'array'});
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, {header: 1, defval: null});
    if (!rows || rows.length < 2) return null;

    // 행 식별: 첫 셀 라벨로 날짜행/데이터행 탐색, 없으면 0/1행 사용
    let dateRow = rows[0] || [];
    let dataRow = rows[1] || [];
    const dIdx = rows.findIndex(r => r && /구분|날짜|일자/.test(String(r[0])));
    const tIdx = rows.findIndex(r => r && /트래픽|설치|건수|방문|수치|count|visit|install/i.test(String(r[0])));
    if (dIdx > -1) dateRow = rows[dIdx];
    if (tIdx > -1) dataRow = rows[tIdx];

    const serials = dateRow.slice(1);
    const values = dataRow.slice(1);

    const months = Array(13).fill(0);     // 1..12
    const weekday = Array(7).fill(0);     // 0=일 .. 6=토
    const weekdayCount = Array(7).fill(0);
    const womSum = Array(6).fill(0);      // 1..5 (월 내 주차)
    const womCount = Array(6).fill(0);
    const daily = [];
    let total = 0, validDays = 0, minDate = null, maxDate = null;

    for (let i = 0; i < serials.length; i++) {
      const s = serials[i];
      if (s == null || isNaN(Number(s))) continue;
      const dc = XLSX.SSF.parse_date_code(Number(s));
      if (!dc || !dc.m || !dc.d) continue;
      const y = !isNaN(fileYear) ? fileYear : dc.y;      // 파일명 연도 우선
      const v = Number(values[i]) || 0;
      const wd = new Date(Date.UTC(y, dc.m - 1, dc.d)).getUTCDay();
      const wom = Math.min(5, Math.ceil(dc.d / 7));      // 1~7=첫째주 … 29~31=다섯째주
      months[dc.m] += v;
      weekday[wd] += v;
      weekdayCount[wd] += 1;
      womSum[wom] += v;
      womCount[wom] += 1;
      total += v;
      validDays += 1;
      const iso = y + '-' + String(dc.m).padStart(2, '0') + '-' + String(dc.d).padStart(2, '0');
      daily.push({date: iso, value: v});
      if (!minDate || iso < minDate) minDate = iso;
      if (!maxDate || iso > maxDate) maxDate = iso;
    }

    let maxMonth = 1;
    for (let m = 2; m <= 12; m++) if (months[m] > months[maxMonth]) maxMonth = m;
    let maxWd = 0;
    for (let w = 1; w < 7; w++) if (weekday[w] > weekday[maxWd]) maxWd = w;

    // 상위/하위 구분 — 월: 상위 6 / 하위 6, 요일: 상위 4 / 하위 3 (값 내림차순 랭킹)
    const monthArr = [];
    for (let m = 1; m <= 12; m++) monthArr.push({key: m, label: m + '월', value: months[m]});
    const monthDesc = monthArr.slice().sort((a, b) => b.value - a.value);
    const topMonths = monthDesc.slice(0, 6);
    const bottomMonths = monthDesc.slice(6).reverse();        // 적은 순(오름차순)
    const highMonthSet = {}; topMonths.forEach(x => { highMonthSet[x.key] = 1; });

    const wdArr = [];
    for (let w = 0; w < 7; w++) wdArr.push({key: w, label: WD_LABELS[w] + '요일', value: weekday[w]});
    const wdDesc = wdArr.slice().sort((a, b) => b.value - a.value);
    const topWd = wdDesc.slice(0, 4);
    const bottomWd = wdDesc.slice(4).reverse();               // 적은 순(오름차순)
    const highWdSet = {}; topWd.forEach(x => { highWdSet[x.key] = 1; });

    // 주차별 최다
    let maxWom = 1;
    for (let k = 2; k <= 5; k++) if (womSum[k] > womSum[maxWom]) maxWom = k;

    return {months, weekday, weekdayCount, daily, total, validDays, minDate, maxDate, maxMonth, maxWd,
      topMonths, bottomMonths, highMonthSet, topWd, bottomWd, highWdSet,
      womSum, womCount, maxWom};
  };

  fmt = (n) => (n == null ? '-' : Number(n).toLocaleString());

  renderRankBox = (title, tagText, items, isLow) => (
    <div className={`ar-rank-box ${isLow ? 'ar-rank-box--low' : 'ar-rank-box--high'}`}>
      <div className="ar-rank-box__head">{title}
        <span className={`ar-rank-box__tag ${isLow ? 'ar-rank-box__tag--low' : ''}`}>{tagText}</span>
      </div>
      <ol className="ar-rank-list">
        {(items || []).map((x, i) => (
          <li key={i}>
            <span className="ar-rank-list__no">{i + 1}</span>
            <span className="ar-rank-list__name">{x.label}</span>
            <span className="ar-rank-list__val">{this.fmt(x.value)}</span>
          </li>
        ))}
      </ol>
    </div>
  );

  renderCharts = () => {
    const r = this.report;
    if (!r) return;
    const metricLabel = this.currentMeta.metric || '수치';
    const mk = (key, ref, config) => {
      if (!ref.current) return;
      if (this._charts[key]) { try { this._charts[key].destroy(); } catch (e) {} }
      this._charts[key] = new Chart(ref.current, config);
    };

    mk('monthly', this.monthlyRef, {
      type: 'bar',
      data: {
        labels: MONTH_LABELS,
        datasets: [{
          label: metricLabel,
          data: r.months.slice(1),
          backgroundColor: r.months.slice(1).map((_, i) => r.highMonthSet[i + 1] ? '#4A6CF7' : '#CBD5E1'),
          hoverBackgroundColor: '#4A6CF7'
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        legend: {display: false},
        scales: {yAxes: [{ticks: {beginAtZero: true, precision: 0}}], xAxes: [{gridLines: {display: false}}]}
      }
    });

    mk('weekday', this.weekdayRef, {
      type: 'bar',
      data: {
        labels: WD_LABELS.map(w => w + '요일'),
        datasets: [{
          label: metricLabel,
          data: r.weekday,
          backgroundColor: r.weekday.map((_, i) => r.highWdSet[i] ? '#10B981' : '#D1D5DB'),
          hoverBackgroundColor: '#10B981'
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        legend: {display: false},
        scales: {yAxes: [{ticks: {beginAtZero: true, precision: 0}}], xAxes: [{gridLines: {display: false}}]}
      }
    });

    // 주차별 (월 내 첫째주~다섯째주) — 최다 주차 강조
    mk('wom', this.womRef, {
      type: 'bar',
      data: {
        labels: WOM_LABELS,
        datasets: [{
          label: metricLabel,
          data: r.womSum.slice(1),
          backgroundColor: r.womSum.slice(1).map((_, i) => (i + 1) === r.maxWom ? '#F59E0B' : '#FDE68A'),
          hoverBackgroundColor: '#F59E0B'
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        legend: {display: false},
        scales: {yAxes: [{ticks: {beginAtZero: true, precision: 0}}], xAxes: [{gridLines: {display: false}}]}
      }
    });

    mk('daily', this.dailyRef, {
      type: 'line',
      data: {
        labels: r.daily.map(d => d.date),
        datasets: [{
          label: metricLabel,
          data: r.daily.map(d => d.value),
          borderColor: '#6366F1',
          backgroundColor: 'rgba(99,102,241,0.08)',
          borderWidth: 1.5,
          pointRadius: 0,
          fill: true,
          lineTension: 0.2
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        legend: {display: false},
        scales: {
          yAxes: [{ticks: {beginAtZero: true, precision: 0}}],
          xAxes: [{ticks: {autoSkip: true, maxTicksLimit: 12, maxRotation: 0}, gridLines: {display: false}}]
        }
      }
    });
  };

  render() {
    const r = this.report;
    const unit = this.currentMeta.unit || '건';
    const metric = this.currentMeta.metric || '방문 트래픽';
    const metrics = this.metricsList();
    const years = this.yearsForMetric(this.selectedMetric);

    return (
      <div className="main-content ar-page">
        <PageTitle/>

        <div className="ar-intro">
          <div className="ar-intro__icon"><FeatherIcon icon="bar-chart-2" size={20}/></div>
          <div>
            <h3 className="ar-intro__title">분석 리포트 — {metric}</h3>
            <p className="ar-intro__desc">연도별 데이터를 분석해 <b>{unit}이(가) 가장 많은 월</b>과 <b>요일</b>을 보여줍니다.
              새 데이터는 <code>public/analysis/</code>에 .xls 추가 후 <code>manifest.json</code>에 등록하면 됩니다.</p>
          </div>
        </div>

        {/* 컨트롤: 지표 + 연도 */}
        <div className="ar-controls">
          <label className="ar-controls__label">지표</label>
          <FormSelect className="ar-controls__select" value={this.selectedMetric}
                      onChange={e => this.onChangeMetric(e.target.value)} disabled={this.loading || metrics.length === 0}>
            {metrics.map((m, i) => (<option key={i} value={m}>{m}</option>))}
          </FormSelect>
          <label className="ar-controls__label">연도</label>
          <FormSelect className="ar-controls__select ar-controls__select--year" value={this.selectedYear}
                      onChange={e => this.onChangeYear(e.target.value)} disabled={this.loading || years.length === 0}>
            {years.map((y, i) => (<option key={i} value={y}>{y}년</option>))}
          </FormSelect>
          <button type="button" className="ar-controls__refresh" onClick={() => this.loadManifest()} disabled={this.loading}>
            <FeatherIcon icon="refresh-cw" size={13}/> 새로고침
          </button>
        </div>

        {this.error ? (
          <div className="ar-error"><FeatherIcon icon="alert-triangle" size={15}/> {this.error}</div>
        ) : null}

        {/* 요약 카드 */}
        <div className="ar-summary">
          <div className="ar-stat ar-stat--primary">
            <span className="ar-stat__label">최다 {unit} 월</span>
            <span className="ar-stat__num">{r ? r.maxMonth + '월' : '-'}</span>
            <span className="ar-stat__sub">{r ? this.fmt(r.months[r.maxMonth]) + ' ' + unit : (this.loading ? '분석 중…' : '-')}</span>
          </div>
          <div className="ar-stat ar-stat--success">
            <span className="ar-stat__label">최다 {unit} 요일</span>
            <span className="ar-stat__num">{r ? WD_LABELS[r.maxWd] + '요일' : '-'}</span>
            <span className="ar-stat__sub">{r ? this.fmt(r.weekday[r.maxWd]) + ' ' + unit : '-'}</span>
          </div>
          <div className="ar-stat ar-stat--week">
            <span className="ar-stat__label">최다 {unit} 주차</span>
            <span className="ar-stat__num">{r ? WOM_LABELS[r.maxWom - 1] : '-'}</span>
            <span className="ar-stat__sub">{r ? this.fmt(r.womSum[r.maxWom]) + ' ' + unit : '-'}</span>
          </div>
          <div className="ar-stat">
            <span className="ar-stat__label">총 {unit}</span>
            <span className="ar-stat__num">{r ? this.fmt(r.total) : '-'}</span>
            <span className="ar-stat__sub">{r ? r.validDays + '일 합계' : '-'}</span>
          </div>
          <div className="ar-stat">
            <span className="ar-stat__label">데이터 기간</span>
            <span className="ar-stat__num ar-stat__num--sm">{r ? r.minDate : '-'}</span>
            <span className="ar-stat__sub">{r ? '~ ' + r.maxDate : '-'}</span>
          </div>
        </div>

        {/* 차트 */}
        <div className="ar-charts">
          <div className="ar-chart-box">
            <div className="ar-chart-box__head">
              <FeatherIcon icon="calendar" size={14}/> 월별 {metric}
              {r ? <span className="ar-chart-box__hl">최다 {r.maxMonth}월</span> : null}
            </div>
            <div className="ar-canvas-wrap"><canvas ref={this.monthlyRef}/></div>
          </div>
          <div className="ar-chart-box">
            <div className="ar-chart-box__head">
              <FeatherIcon icon="clock" size={14}/> 요일별 {metric}
              {r ? <span className="ar-chart-box__hl ar-chart-box__hl--green">최다 {WD_LABELS[r.maxWd]}요일</span> : null}
            </div>
            <div className="ar-canvas-wrap"><canvas ref={this.weekdayRef}/></div>
          </div>
          <div className="ar-chart-box">
            <div className="ar-chart-box__head">
              <FeatherIcon icon="layers" size={14}/> 주차별 {metric}
              {r ? <span className="ar-chart-box__hl ar-chart-box__hl--amber">최다 {WOM_LABELS[r.maxWom - 1]}</span> : null}
            </div>
            <div className="ar-canvas-wrap"><canvas ref={this.womRef}/></div>
          </div>
        </div>

        {/* 상위/하위 구분 */}
        {r ? (
          <div className="ar-rankgrid">
            {this.renderRankBox(unit + ' 많은 월', '상위 6', r.topMonths, false)}
            {this.renderRankBox(unit + ' 적은 월', '하위 6', r.bottomMonths, true)}
            {this.renderRankBox(unit + ' 많은 요일', '상위 4', r.topWd, false)}
            {this.renderRankBox(unit + ' 적은 요일', '하위 3', r.bottomWd, true)}
          </div>
        ) : null}

        <div className="ar-chart-box ar-chart-box--wide">
          <div className="ar-chart-box__head"><FeatherIcon icon="trending-up" size={14}/> 일별 추이</div>
          <div className="ar-canvas-wrap ar-canvas-wrap--tall"><canvas ref={this.dailyRef}/></div>
        </div>

        {this.loading ? <div className="ar-loading"><FeatherIcon icon="loader" size={16}/> 데이터를 분석하는 중…</div> : null}
      </div>
    );
  }
}

export default AnalysisReport;
